import { addThrottledEvent, Collapse, isMobile, Module, Node, VariantType } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { INavigationMenuViewRootProps, INavigationState } from './navigation-menu-utilities';
import { IMenuItemData } from '@msdyn365-commerce-modules/navigation-menu';
import { getCategoriesWithProducts, ICategoryHasProductsMap } from '../../utils/category-product-count';
import { IPhbMobileMenuContext, PhbMobileMenuContext } from '../../utils/phb-mobile-menu-context';
import { IActionContext } from '@msdyn365-commerce/core';

/**
 * Mega menu (columns) from this width and up. Below it the menu becomes a drill-down list.
 * Keep in sync with header.scss (max-width: 1023px / min-width: 1024px) and header.view.tsx.
 */
const MEGA_MENU_MIN_WIDTH = 1024;

/** The CMS "All" sub item is shown with this label, at the bottom of its list. */
const VIEW_ALL_LABEL = 'View all';

const PRODUCTS_NODE_ID = '__phb-products';

interface IExtendedNavigationState extends INavigationState {
    categoriesWithProducts: ICategoryHasProductsMap;
    categoriesLoaded: boolean;
    /** Ids of the rows the drill-down has opened, first = top level. Empty = first screen. */
    mobilePath: string[];
}

/** One row of the drill-down list */
interface IMobileNode {
    id: string;
    text: string;
    href?: string;
    viewAllHref?: string;
    children: IMobileNode[];
}

export class NavigationMenuRootDisabled extends React.PureComponent<INavigationMenuViewRootProps, IExtendedNavigationState> {
    /** Gives us Home / Contact us links and the drawer state from header.view.tsx (optional) */
    static contextType = PhbMobileMenuContext;

    private navRef = React.createRef<HTMLDivElement>(); // Entire navigation container
    private mobileListRef = React.createRef<HTMLElement>(); // Drill-down list (tablet and below)
    private customResizeThrottledEventHandler?: (event: Event) => void;
    private wasMenuOpen = false;

    constructor(props: INavigationMenuViewRootProps) {
        super(props);

        this.state = {
            activeMenu: undefined,
            parentMenu: undefined,
            drawerKeyValue: {},
            mobileViewLabelText: '',
            isOnlyMobile: this.getIsMobile(),
            isNavOpen: true, // desktop stays open
            categoryImage: [],
            rightCardTopPx: 0,
            categoriesWithProducts: {},
            categoriesLoaded: false,
            mobilePath: []
        };
    }

    private _menuContext(): IPhbMobileMenuContext | undefined {
        return this.context as IPhbMobileMenuContext | undefined;
    }

    /** Initial value only (works on the server too). The real value is set from the window width after mount. */
    private getIsMobile(): boolean {
        return (
            isMobile({
                variant: VariantType.Browser,
                context: this.props.navProps.context.request
            }) === 'xs'
        );
    }

    private _isBelowMegaBreakpoint(): boolean {
        return typeof window !== 'undefined' && window.innerWidth < MEGA_MENU_MIN_WIDTH;
    }

    componentDidMount(): void {
        this.customResizeThrottledEventHandler = addThrottledEvent(window, 'resize', this._updateViewport as EventListener);

        this._updateViewport();
        this._loadCategoryAvailability();
    }

    componentDidUpdate(_prevProps: unknown, prevState: IExtendedNavigationState): void {
        if (!this.state.isOnlyMobile) {
            return;
        }

        const menu = this._menuContext();
        const isOpen = !!menu?.isMenuOpen;

        // Drawer was closed: start from the first screen next time it opens
        if (this.wasMenuOpen && !isOpen && this.state.mobilePath.length > 0) {
            this.setState({ mobilePath: [] });
        }
        this.wasMenuOpen = isOpen;

        if (prevState.mobilePath.length !== this.state.mobilePath.length) {
            // The Back arrow lives in the drawer's top bar (next to the close button)
            menu?.setBackHandler(this.state.mobilePath.length > 0 ? this._goBack : undefined);

            // Keep keyboard / screen reader users at the top of the new screen
            if (isOpen) {
                this.mobileListRef.current?.focus();
            }
        }
    }

    componentWillUnmount(): void {
        window.removeEventListener('resize', this.customResizeThrottledEventHandler!, false);
    }

    private _extractIdsFromUrl(linkURL?: string): { categoryId?: number; catalogId?: number } {
        if (!linkURL) {
            return {};
        }

        const categoryMatch = linkURL.match(/\/(\d+)\.c(?:\?|$)/);
        const catalogMatch = linkURL.match(/[?&]catalogid=(\d+)/i);

        const categoryId = categoryMatch ? Number(categoryMatch[1]) : undefined;
        const catalogId = catalogMatch ? Number(catalogMatch[1]) : undefined;

        return {
            categoryId: Number.isFinite(categoryId as number) ? categoryId : undefined,
            catalogId: Number.isFinite(catalogId as number) ? catalogId : undefined
        };
    }

    private _collectCategoryEntries(items: IMenuItemData[]): Array<{ categoryId: number; catalogId: number }> {
        const entries: Array<{ categoryId: number; catalogId: number }> = [];

        items.forEach(item => {
            const { categoryId, catalogId } = this._extractIdsFromUrl(item.linkURL);
            if (categoryId !== undefined && catalogId !== undefined) {
                entries.push({ categoryId, catalogId });
            }
            if (Array.isArray(item.subMenu) && item.subMenu.length > 0) {
                entries.push(...this._collectCategoryEntries(item.subMenu));
            }
        });

        return entries;
    }

    private async _loadCategoryAvailability(): Promise<void> {
        const { context, menuItemData } = this.props.navProps;
        const actionContext: IActionContext = context.actionContext;
        const channelId = context.request.apiSettings.channelId;

        const allItems = (menuItemData ?? []) as IMenuItemData[];
        const entries = this._collectCategoryEntries(allItems);

        if (entries.length === 0) {
            this.setState({ categoriesLoaded: true });
            return;
        }

        const categoriesWithProducts = await getCategoriesWithProducts(actionContext, channelId, entries);
        this.setState({ categoriesWithProducts, categoriesLoaded: true });
    }

    /** Recursively removes categories with no products; keeps a parent if any descendant still has products */
    private _filterCategoriesWithProducts = (items: IMenuItemData[]): IMenuItemData[] => {
        const { categoriesWithProducts } = this.state;

        return items.reduce<IMenuItemData[]>((acc, item) => {
            const filteredChildren = Array.isArray(item.subMenu) && item.subMenu.length > 0
                ? this._filterCategoriesWithProducts(item.subMenu)
                : [];

            const { categoryId } = this._extractIdsFromUrl(item.linkURL);
            const ownHasProducts = categoryId !== undefined
                ? !!categoriesWithProducts[categoryId]
                : false;

            const hasProducts = ownHasProducts || filteredChildren.length > 0;

            if (hasProducts) {
                acc.push({ ...item, subMenu: filteredChildren });
            }
            return acc;
        }, []);
    };

    /** Switch between mega menu and drill-down when the window crosses the breakpoint. */
    private _updateViewport = () => {
        const isOnlyMobile = this._isBelowMegaBreakpoint();

        if (isOnlyMobile !== this.state.isOnlyMobile) {
            this._menuContext()?.setBackHandler(undefined);
            // Always start from the top level after switching layouts
            this.setState({ isOnlyMobile, mobilePath: [], activeMenu: undefined, parentMenu: undefined, mobileViewLabelText: '' });
        }
    };

    render(): JSX.Element | null {
        return (
            <Node className='nav-menu-container' ref={this.navRef}>
                {this.state.isOnlyMobile ? this._renderMobile() : this._renderDesktop()}
            </Node>
        );
    }

    private _isViewAll(item: IMenuItemData): boolean {
        return (item.linkText ?? '').trim().toLowerCase() === 'all';
    }

    /** ------------------------------------------------------------------
     * DESKTOP (>= 1024px): every category is a column, sub categories listed underneath,
     * "View all" closes each column.
     * ------------------------------------------------------------------ */
    private _renderDesktop() {
        const { Navigation, MenuList, menuItemData } = this.props.navProps;
        const rawItems = this._getDesktopItems((menuItemData ?? []) as IMenuItemData[]);
        const items = this._filterCategoriesWithProducts(rawItems);

        return (
            <Module {...Navigation} className='phb-nav phb-nav--desktop'>
                <Collapse className='ms-header__collapsible-hamburger' isOpen={true}>
                    <div className='phb-mega-container'>
                        <Node {...MenuList} className='phb-nav__root-list--desktop phb-mega'>
                            {items.map((item: IMenuItemData, index: number) => this._renderMegaColumn(item, index))}
                        </Node>
                    </div>
                </Collapse>
            </Module>
        );
    }

    private _renderMegaColumn(item: IMenuItemData, index: number): JSX.Element {
        const subs = Array.isArray(item.subMenu) ? item.subMenu : [];
        const viewAll = subs.find(child => this._isViewAll(child));
        const links = subs.filter(child => !this._isViewAll(child));
        const viewAllHref = viewAll?.linkURL ?? item.linkURL;
        const showViewAll = !!viewAllHref && (links.length > 0 || !!viewAll);

        return (
            <li key={item.id ?? index} className='phb-mega__col'>
                {item.linkURL ? (
                    <a href={item.linkURL} className='phb-mega__heading'>
                        {item.linkText}
                    </a>
                ) : (
                    <span className='phb-mega__heading'>{item.linkText}</span>
                )}

                {(links.length > 0 || showViewAll) && (
                    <ul className='phb-mega__list'>
                        {links.map((child: IMenuItemData, i: number) => (
                            <li key={child.id ?? i}>
                                <a href={child.linkURL ?? '#'} className='phb-mega__link'>
                                    {child.linkText}
                                </a>
                            </li>
                        ))}

                        {showViewAll && (
                            <li key='view-all' className='phb-mega__item--all'>
                                <a href={viewAllHref} className='phb-mega__link phb-mega__link--all'>
                                    {VIEW_ALL_LABEL}
                                </a>
                            </li>
                        )}
                    </ul>
                )}
            </li>
        );
    }

    /**
     * Normalize menu shape for the custom menu.
     * CMS often provides a root item whose children contain the actual categories.
     */
    private _getDesktopItems(menuItemData: IMenuItemData[]): IMenuItemData[] {
        if (!Array.isArray(menuItemData) || menuItemData.length === 0) {
            return [];
        }

        const hasTopLevelChildren = menuItemData.some(item => Array.isArray(item.subMenu) && item.subMenu.length > 0);
        if (hasTopLevelChildren && menuItemData.length === 1) {
            const directChildren = (menuItemData[0].subMenu ?? []).filter(
                subMenuItem => !!subMenuItem && (!!subMenuItem.linkText || Array.isArray(subMenuItem.subMenu))
            );
            if (directChildren.length > 0) {
                return directChildren;
            }

            return this._collectDisplayableChildren(menuItemData[0]);
        }

        return menuItemData;
    }

    private _collectDisplayableChildren(root: IMenuItemData): IMenuItemData[] {
        const queue = [...(root.subMenu ?? [])];
        const result: IMenuItemData[] = [];

        while (queue.length > 0) {
            const current = queue.shift();
            if (!current) {
                continue;
            }

            if (!!current.linkText || (Array.isArray(current.subMenu) && current.subMenu.length > 0)) {
                result.push(current);
            }

            if (Array.isArray(current.subMenu) && current.subMenu.length > 0) {
                queue.push(...current.subMenu);
            }
        }

        return result;
    }

    /** ------------------------------------------------------------------
     * TABLET AND BELOW (< 1024px): drill-down inside the header drawer.
     *   Screen 1: Home, Products >, Contact us   (from header.view.tsx via context)
     *   Screen 2: categories
     *   Screen 3: sub categories (and deeper, if the CMS has more levels)
     * "View all" closes every list that has a link to give. The Back arrow is in the drawer's top bar.
     * ------------------------------------------------------------------ */
    private _renderMobile() {
        const { Navigation, MenuList, menuItemData } = this.props.navProps;
        const menu = this._menuContext();

        const rawItems = this._getDesktopItems((menuItemData ?? []) as IMenuItemData[]);
        const categories = this._toMobileNodes(this._filterCategoriesWithProducts(rawItems));
        const root = this._buildMobileRoot(categories, menu);

        // Walk down the opened path
        let level = root;
        let parent: IMobileNode | undefined;
        for (let i = 0; i < this.state.mobilePath.length; i++) {
            const next = level.find(node => node.id === this.state.mobilePath[i]);
            if (!next) {
                break;
            }
            parent = next;
            level = next.children;
        }

        const viewAllHref = parent?.viewAllHref;

        return (
            <Module {...Navigation} className={classnames(Navigation.className, 'phb-nav--mobile')}>
                <Node
                    {...MenuList}
                    className='phb-m-list'
                    tabIndex='-1'
                    key={this.state.mobilePath.join('>') || 'root'}
                    ref={this.mobileListRef}
                >
                    {/* Only when the nav is used without the header drawer */}
                    {!menu && this.state.mobilePath.length > 0 && (
                        <li key='back'>
                            <button type='button' className='phb-m-row phb-m-row--back' onClick={this._goBack}>
                                Back
                            </button>
                        </li>
                    )}

                    {level.map((node: IMobileNode) => this._renderMobileRow(node))}

                    {viewAllHref && (
                        <li key='view-all' className='phb-m-viewall'>
                            <a href={viewAllHref}>{VIEW_ALL_LABEL}</a>
                        </li>
                    )}
                </Node>
            </Module>
        );
    }

    private _buildMobileRoot(categories: IMobileNode[], menu?: IPhbMobileMenuContext): IMobileNode[] {
        if (!menu) {
            return categories;
        }

        const root: IMobileNode[] = [];

        if (menu.homeLink) {
            root.push({ id: '__phb-home', text: menu.homeLink.text, href: menu.homeLink.href, children: [] });
        }

        root.push({
            id: PRODUCTS_NODE_ID,
            text: menu.productsText,
            href: menu.productsHref,
            viewAllHref: menu.productsHref,
            children: categories
        });

        if (menu.contactLink) {
            root.push({ id: '__phb-contact', text: menu.contactLink.text, href: menu.contactLink.href, children: [] });
        }

        return root;
    }

    /** CMS items -> rows. The "All" child becomes the parent's View all link instead of a row. */
    private _toMobileNodes(items: IMenuItemData[]): IMobileNode[] {
        return items.map((item: IMenuItemData, index: number) => {
            const subs = Array.isArray(item.subMenu) ? item.subMenu : [];
            const allChild = subs.find(child => this._isViewAll(child));
            const others = subs.filter(child => !this._isViewAll(child));

            return {
                id: String(item.id ?? index),
                text: item.linkText ?? '',
                href: item.linkURL,
                viewAllHref: allChild?.linkURL ?? item.linkURL,
                children: this._toMobileNodes(others)
            };
        });
    }

    private _renderMobileRow(node: IMobileNode): JSX.Element {
        return (
            <li key={node.id}>
                {node.children.length > 0 ? (
                    <button type='button' className='phb-m-row phb-m-row--parent' onClick={this._openMobileNode(node.id)}>
                        <span>{node.text}</span>
                    </button>
                ) : (
                    <a className='phb-m-row' href={node.href ?? '#'}>
                        {node.text}
                    </a>
                )}
            </li>
        );
    }

    private _openMobileNode = (id: string) => () => {
        this.setState(prev => ({ mobilePath: [...prev.mobilePath, id] }));
    };

    private _goBack = () => {
        this.setState(prev => ({ mobilePath: prev.mobilePath.slice(0, -1) }));
    };
}

export default NavigationMenuRootDisabled;
